import { DQMRow } from '@fullstory/proto/fs/services/dx-objects';
import { ElementPropertyRuleRecord } from '@fullstory/proto/fs/services/search-settings';
import {
  definedEventSelectors,
  elementPropertyRulesSelectors,
  getAllCustomEventNames,
  getCustomEventProps,
  getElementProps,
  getHiddenVars,
  getPageProps,
} from '@fullstory/redux';
import { parentHideKey } from '@fullstory/search-vars';
import { createSelector } from 'reselect';
import { APIEvent, EventRow, PropertyRow } from './types';
import { buildPropertyRows, propertiesWithPending } from './util';

const { getUnarchivedElementPropertyRules } = elementPropertyRulesSelectors;

export const getEventPropertiesByEvent = createSelector(
  getCustomEventProps,
  getHiddenVars,
  (customEventProps, hidden) => {
    const byEvent = new Map<string, PropertyRow[]>();
    for (const [event, properties] of customEventProps.entries()) {
      if (event) {
        byEvent.set(event, buildPropertyRows(properties, hidden));
      }
    }
    return byEvent;
  },
);

export const getPagePropertiesByPage = createSelector(
  getPageProps,
  getHiddenVars,
  (pageProps, hidden) => {
    const byPage = new Map<number, PropertyRow[]>();
    for (const [page, properties] of pageProps.entries()) {
      if (page) {
        byPage.set(page, buildPropertyRows(properties, hidden));
      }
    }
    return byPage;
  },
);

export const getElementPropertiesByElement = createSelector(
  getElementProps,
  getHiddenVars,
  getUnarchivedElementPropertyRules,
  (elementProps, hidden, propertyRules) => {
    const propertyRulesByElement = new Map<string, ElementPropertyRuleRecord[]>();
    for (const rule of propertyRules) {
      const element = rule.elementPropertyRule?.namedElementId;
      if (element) {
        if (!propertyRulesByElement.has(element)) {
          propertyRulesByElement.set(element, []);
        }
        propertyRulesByElement.get(element)?.push(rule);
      }
    }
    const byElement = new Map<string, PropertyRow[]>();
    for (const [element, properties] of elementProps.entries()) {
      if (element) {
        const propertyRules = propertyRulesByElement.get(element) ?? [];
        const { mergedProperties, pending } = propertiesWithPending(properties, propertyRules);
        byElement.set(element, buildPropertyRows(mergedProperties, hidden, pending));
      }
    }
    return byElement;
  },
);

const getCustomApiEvents = createSelector(
  getAllCustomEventNames,
  getHiddenVars,
  (customEventNames, hiddenVars) => {
    const events: APIEvent[] = customEventNames.map(name => ({
      type: 'customApiEvent',
      name,
      lastUpdated: null,
      lastUpdatedBy: '',
      archived: hiddenVars.has(parentHideKey(name)),
      eventSupport: DQMRow.EventSupport.EVENT_SUPPORT_NOT_APPLICABLE,
    }));
    return events;
  },
);

export const getCustomApiEventsMap = createSelector(getCustomApiEvents, apiEvents => {
  const eventsMap = new Map<string, APIEvent>();
  apiEvents.forEach(apiEvent => eventsMap.set(apiEvent.name, apiEvent));
  return eventsMap;
});

export const getDefinedEvents = createSelector(
  definedEventSelectors.getAllDefinedEvents,
  definedEvents => {
    const events: EventRow[] = definedEvents
      .filter(e => !e.definedEventMetadata?.fsManaged)
      .map(e => ({
        type: 'definedEvent',
        id: e.definedEvent?.id || '',
        name: e.definedEvent?.name || '',
        lastUpdated: e.metadata?.lastUpdated,
        lastUpdatedBy: e.metadata?.lastUpdatedBy,
        archived: e.metadata?.archived || false,
        description: e.definedEvent?.description || '',
        events: e.definedEvent?.events || [],
        auditMetadata: e.metadata,
        eventSupport: DQMRow.EventSupport.EVENT_SUPPORT_NOT_APPLICABLE,
      }));
    return events;
  },
);

export const getEventTable = createSelector(
  getCustomApiEvents,
  getDefinedEvents,
  (customApiEvents, definedEvents) => {
    const events: EventRow[] = [...customApiEvents, ...definedEvents];
    return events;
  },
);
